import { useUploadStore } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

type UploadParams = {
  files: File[];
  certificateType: string;
  storeKey?: string;
  sasTokenPrefix?: SasTokenPrefix;
};

const UPLOAD_OPERATIONS_DOCUMENTS_TRANSACTION_CONTEXT = {
  module: "vessel_call",
  screen: "Operations",
  action: "upload_operations_documents",
  entityType: "document",
};

export const useUploadOperationsDocuments = () => {
  const mutation = useMutation({
    mutationFn: ({
      files,
      certificateType,
      sasTokenPrefix = SasTokenPrefix.VESSEL_CALL,
    }: UploadParams) =>
      runTransaction(UPLOAD_OPERATIONS_DOCUMENTS_TRANSACTION_CONTEXT, async (transactionId) => {
        const documentUrls: string[] = [];

        for (const file of files) {
          const { blobUrl, sasToken } = await getSasToken(
            {
              prefix: sasTokenPrefix,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            },
            ...(transactionId ? [transactionId] : []),
          );

          const result = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType,
          });

          result.forEach((entry) => documentUrls.push(entry.documentUrl));
        }

        return documentUrls;
      }),
    onMutate: ({ storeKey }) => {
      if (storeKey) {
        useUploadStore.getState().beginUploadInFlight(storeKey);
      }
    },
    onSettled: (_data, _error, variables) => {
      if (variables?.storeKey) {
        useUploadStore.getState().endUploadInFlight(variables.storeKey);
      }
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
};
